'use client'

import Image from 'next/image'
import { motion } from 'framer-motion'
import { ArrowRight, ImageIcon } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { Button } from '@/components/ui/button'

interface FeatureShowcaseItem {
  title: string
  description: string
  cta?: {
    text: string
    href: string
  }
  media:
    | { type: 'placeholder' }
    | { type: 'image'; src: string; alt: string }
}

interface FeatureShowcaseProps {
  features: FeatureShowcaseItem[]
  variant?: 'primary' | 'ai'
}

export function FeatureShowcase({ features, variant = 'primary' }: FeatureShowcaseProps) {
  const isAi = variant === 'ai'

  return (
    <>
      {features.map((feature, index) => {
        const isReversed = index % 2 === 1

        return (
          <Section key={feature.title} background={index % 2 === 0 ? 'white' : 'gray'}>
            <Container>
              <div
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  isReversed ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: isReversed ? 20 : -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className={isReversed ? 'lg:col-start-2' : ''}
                >
                  <h2 className="text-display-1 text-stone-900 mb-4">
                    {feature.title}
                  </h2>
                  <p className="text-lg text-stone-600 mb-6 leading-relaxed">
                    {feature.description}
                  </p>
                  {feature.cta && (
                    <Button
                      href={feature.cta.href}
                      external={feature.cta.href.startsWith('http')}
                      variant={isAi ? 'ai' : 'primary'}
                    >
                      {feature.cta.text}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  )}
                </motion.div>

                {/* Media */}
                <motion.div
                  initial={{ opacity: 0, x: isReversed ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className={isReversed ? 'lg:col-start-1 lg:row-start-1' : ''}
                >
                  <div
                    className={`relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl ${
                      isAi ? 'shadow-secondary-200/30' : 'shadow-primary-200/30'
                    }`}
                  >
                    {feature.media.type === 'placeholder' && (
                      <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                        <div className="text-stone-400 text-center">
                          <ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-50" />
                          <span className="text-sm font-medium">Image coming soon</span>
                        </div>
                      </div>
                    )}

                    {feature.media.type === 'image' && (
                      <Image
                        src={feature.media.src}
                        alt={feature.media.alt}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        unoptimized={feature.media.src.startsWith('http') && !feature.media.src.includes('shopify.com')}
                      />
                    )}
                  </div>
                </motion.div>
              </div>
            </Container>
          </Section>
        )
      })}
    </>
  )
}

// Single feature showcase for one-off sections
interface SingleFeatureShowcaseProps {
  title: string
  description: string
  cta?: {
    text: string
    href: string
  }
  media:
    | { type: 'placeholder' }
    | { type: 'image'; src: string; alt: string }
  reverse?: boolean
  variant?: 'primary' | 'ai'
  background?: 'white' | 'gray'
}

export function SingleFeatureShowcase({
  title,
  description,
  cta,
  media,
  reverse = false,
  variant = 'primary',
  background = 'white',
}: SingleFeatureShowcaseProps) {
  const isAi = variant === 'ai'

  return (
    <Section background={background}>
      <Container>
        <div
          className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
            reverse ? 'lg:grid-flow-dense' : ''
          }`}
        >
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: reverse ? 20 : -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={reverse ? 'lg:col-start-2' : ''}
          >
            <h2 className="text-display-1 text-stone-900 mb-4">
              {title}
            </h2>
            <p className="text-lg text-stone-600 mb-6 leading-relaxed">
              {description}
            </p>
            {cta && (
              <Button
                href={cta.href}
                external={cta.href.startsWith('http')}
                variant={isAi ? 'ai' : 'primary'}
              >
                {cta.text}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </motion.div>

          {/* Media */}
          <motion.div
            initial={{ opacity: 0, x: reverse ? -20 : 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={reverse ? 'lg:col-start-1 lg:row-start-1' : ''}
          >
            <div
              className={`relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl ${
                isAi ? 'shadow-secondary-200/30' : 'shadow-primary-200/30'
              }`}
            >
              {media.type === 'placeholder' && (
                <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                  <div className="text-stone-400 text-center">
                    <ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-50" />
                    <span className="text-sm font-medium">Image coming soon</span>
                  </div>
                </div>
              )}

              {media.type === 'image' && (
                <Image
                  src={media.src}
                  alt={media.alt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  unoptimized={media.src.startsWith('http') && !media.src.includes('shopify.com')}
                />
              )}
            </div>
          </motion.div>
        </div>
      </Container>
    </Section>
  )
}
